import React, { useState } from 'react';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { useIncome } from './useIncome';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { IncomeType } from './api/useIncomeQuery';
import { Employment } from './tables/Employment/Employment';
import { Business } from './tables/Business/Business';
import { RealEstate } from './tables/RealEstate/RealEstate';
import { SocialSecurity } from './tables/SocialSecurity/SocialSecurity';
import { OtherIncome } from './tables/OtherIncome/OtherIncome';
import { IncomeToDelete, IncomeToUpsert } from './Income.meta';
import { DeleteIncomeModal } from './components/DeleteIncomeModal/DeleteIncomeModal';
import { UpsertIncomeModal } from './components/UpsertIncomeModal/UpsertIncomeModal';
import { ProfileTablesLoading } from '../components/ProfileTablesLoading/ProfileTablesLoading';

const TablesDict = {
  [IncomeType.Salary]: Employment,
  [IncomeType.Business]: Business,
  [IncomeType.RealEstate]: RealEstate,
  [IncomeType.SocialSecurity]: SocialSecurity,
  [IncomeType.Other]: OtherIncome,
};

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const Income = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const { isSuccess } = useIncome({ clientId });

  const [incomeToUpsert, setIncomeToUpsert] = useState<IncomeToUpsert | null>(null);

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Income"
          description="Add any salaries, business income and real estate income, social security and other income."
        >
          {isSuccess && (
            <Button leftIcon="fas fa-plus" onClick={() => setIncomeToUpsert({})}>
              Add Income
            </Button>
          )}
        </ProfilePageHeader>

        <IncomeContent
          clientId={clientId}
          workflowProps={workflowProps}
          incomeToUpsert={incomeToUpsert}
          setIncomeToUpsert={setIncomeToUpsert}
        />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};

interface ContentProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  incomeToUpsert: IncomeToUpsert | null;
  setIncomeToUpsert: (incomeToUpsert: IncomeToUpsert | null) => void;
}

const IncomeContent = ({
  clientId,
  workflowProps,
  incomeToUpsert,
  setIncomeToUpsert,
}: ContentProps) => {
  const [incomeToDelete, setIncomeToDelete] = useState<IncomeToDelete | null>(null);

  const { isLoading, isError, refetch, data } = useIncome({ clientId });

  if (isLoading) {
    return <ProfileTablesLoading isWorkflow={!!workflowProps} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Box display="flex" flexDirection="column" css="gap: 32px">
        {data.Options.IncomeTypeOptions.map(incomeType => {
          const IncomeTable = TablesDict[incomeType.value];

          return (
            <IncomeTable
              key={incomeType.value}
              clientId={clientId}
              incomeType={incomeType.value}
              data={data}
              setIncomeToUpsert={setIncomeToUpsert}
              setIncomeToDelete={setIncomeToDelete}
              workflowProps={workflowProps}
            />
          );
        })}
      </Box>

      {incomeToUpsert && (
        <UpsertIncomeModal
          clientId={clientId}
          income={incomeToUpsert}
          data={data}
          onClose={() => setIncomeToUpsert(null)}
          onSuccess={() => setIncomeToUpsert(null)}
          onIncomeTypeChange={incomeType => setIncomeToUpsert({ IncomeType: incomeType })}
          incomeToDelete={incomeToDelete}
          setIncomeToDelete={setIncomeToDelete}
          workflowProps={workflowProps}
        />
      )}

      {incomeToDelete && (
        <DeleteIncomeModal
          clientId={clientId}
          income={incomeToDelete}
          onClose={() => setIncomeToDelete(null)}
          onDelete={() => {
            setIncomeToDelete(null);
            setIncomeToUpsert(null);
          }}
        />
      )}
    </>
  );
};
